import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// 🎨 Visual Design - Gradients, Border Radius, Shadows, Typography, Spacing
export const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: var(--space-4);

  @media (max-width: 768px) {
    padding: var(--space-2);
  }
`;

export const ModalContent = styled.div`
  background: linear-gradient(135deg, var(--surface-color) 0%, var(--background-color) 100%);
  border-radius: var(--radius-2xl);
  box-shadow: var(--shadow-xl);
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  border: 1px solid var(--border-color-light);
  position: relative;

  /* Modern scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--neutral-300);
    border-radius: var(--radius-full);
  }

  &::-webkit-scrollbar-thumb:hover {
    background: var(--neutral-400);
  }

  @media (max-width: 768px) {
    max-width: 95vw;
    max-height: 95vh;
    border-radius: var(--radius-xl);
  }
`;

export const ModalHeader = styled.div`
  padding: var(--space-6);
  border-bottom: 1px solid var(--border-color-light);
  background: linear-gradient(135deg, var(--primary-50) 0%, var(--primary-100) 100%);
  border-radius: var(--radius-2xl) var(--radius-2xl) 0 0;
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 768px) {
    padding: var(--space-4);
  }
`;

export const ModalTitle = styled.h2`
  font-size: var(--text-xl);
  font-weight: 600;
  color: var(--text-color);
  margin: 0;
  font-family: var(--font-heading-bold);
  display: flex;
  align-items: center;
  gap: var(--space-3);

  span {
    background: linear-gradient(135deg, var(--primary-600), var(--primary-700));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  @media (max-width: 768px) {
    font-size: var(--text-lg);
  }
`;

export const CloseButton = styled.button`
  background: var(--surface-color);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-lg);
  width: 40px;
  height: 40px;
  cursor: pointer;
  color: var(--text-secondary);
  font-size: var(--text-lg);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: var(--shadow-sm);

  &:hover {
    background: var(--error-50);
    color: var(--error-600);
    border-color: var(--error-300);
    transform: scale(1.05);
    box-shadow: var(--shadow-md);
  }

  &:active {
    transform: scale(0.95);
  }

  &:focus-visible {
    outline: 2px solid var(--primary-600);
    outline-offset: 2px;
  }

  @media (max-width: 768px) {
    width: 36px;
    height: 36px;
    font-size: var(--text-base);
  }
`;

export const CharacterAvatar = styled.div`
  width: 80px;
  height: 80px;
  border-radius: var(--radius-full);
  background: linear-gradient(135deg, var(--primary-600), var(--primary-700));
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: var(--text-2xl);
  font-weight: 600;
  margin: 0 auto var(--space-4) auto;
  border: 3px solid var(--primary-100);
  box-shadow: var(--shadow-lg);

  @media (max-width: 768px) {
    width: 60px;
    height: 60px;
    font-size: var(--text-xl);
  }
`;

export const Form = styled.form`
  padding: var(--space-6);
  display: flex;
  flex-direction: column;
  gap: var(--space-6);

  @media (max-width: 768px) {
    padding: var(--space-4);
    gap: var(--space-4);
  }
`;

export const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
`;

export const SectionTitle = styled.h3`
  font-size: var(--text-lg);
  font-weight: 600;
  color: var(--text-color);
  margin: 0;
  font-family: var(--font-heading-medium);
  display: flex;
  align-items: center;
  gap: var(--space-2);

  &::before {
    content: '';
    width: 4px;
    height: 16px;
    background: linear-gradient(135deg, var(--primary-600), var(--primary-700));
    border-radius: var(--radius-sm);
    flex-shrink: 0;
  }

  @media (max-width: 768px) {
    font-size: var(--text-base);
  }
`;

export const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
`;

export const Input = styled.input`
  width: 100%;
  padding: var(--space-3) var(--space-4);
  border: 1px solid var(--border-color-light);
  border-radius: var(--radius-lg);
  font-size: var(--text-base);
  font-family: var(--font-family);
  font-weight: 400;
  background-color: var(--surface-color);
  color: var(--text-color);
  box-sizing: border-box;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: var(--shadow-sm);

  &:focus {
    outline: none;
    border-color: var(--primary-600);
    box-shadow: 0 0 0 3px var(--primary-100), var(--shadow-md);
    transform: translateY(-1px);
  }

  &:hover:not(:focus) {
    border-color: var(--primary-300);
  }

  &::placeholder {
    color: var(--text-secondary);
    opacity: 0.7;
  }

  @media (max-width: 768px) {
    padding: var(--space-3);
    font-size: var(--text-sm);
    min-height: 44px; /* Touch friendly */
  }
`;

export const TextArea = styled.textarea`
  width: 100%;
  padding: var(--space-3) var(--space-4);
  border: 1px solid var(--border-color-light);
  border-radius: var(--radius-lg);
  font-size: var(--text-base);
  font-family: var(--font-family);
  font-weight: 400;
  background-color: var(--surface-color);
  color: var(--text-color);
  box-sizing: border-box;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: var(--shadow-sm);
  resize: vertical;
  min-height: 100px;

  &:focus {
    outline: none;
    border-color: var(--primary-600);
    box-shadow: 0 0 0 3px var(--primary-100), var(--shadow-md);
    transform: translateY(-1px);
  }

  &:hover:not(:focus) {
    border-color: var(--primary-300);
  }

  &::placeholder {
    color: var(--text-secondary);
    opacity: 0.7;
  }

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--neutral-300);
    border-radius: var(--radius-full);
  }

  &::-webkit-scrollbar-thumb:hover {
    background: var(--neutral-400);
  }

  @media (max-width: 768px) {
    padding: var(--space-3);
    font-size: var(--text-sm);
    min-height: 80px;
  }
`;

export const Select = styled.select`
  width: 100%;
  padding: var(--space-3) var(--space-4);
  border: 1px solid var(--border-color-light);
  border-radius: var(--radius-lg);
  font-size: var(--text-base);
  font-family: var(--font-family);
  font-weight: 400;
  background-color: var(--surface-color);
  color: var(--text-color);
  box-sizing: border-box;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: var(--shadow-sm);
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: var(--primary-600);
    box-shadow: 0 0 0 3px var(--primary-100), var(--shadow-md);
    transform: translateY(-1px);
  }

  &:hover:not(:focus) {
    border-color: var(--primary-300);
  }

  @media (max-width: 768px) {
    padding: var(--space-3);
    font-size: var(--text-sm);
    min-height: 44px; /* Touch friendly */
  }
`;

export const ButtonGroup = styled.div`
  display: flex;
  gap: var(--space-3);
  justify-content: flex-end;
  margin-top: var(--space-4);

  @media (max-width: 768px) {
    flex-direction: column;
    gap: var(--space-2);
  }
`;

export const PrimaryButton = styled.button`
  padding: var(--space-3) var(--space-6);
  background: linear-gradient(135deg, var(--primary-600) 0%, var(--primary-700) 100%);
  color: white;
  border: none;
  border-radius: var(--radius-lg);
  font-size: var(--text-base);
  font-family: var(--font-heading-regular);
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 12px rgba(90, 99, 230, 0.3);
  position: relative;
  overflow: hidden;
  min-height: 44px; /* Touch friendly */

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.2) 0%, transparent 50%);
    border-radius: inherit;
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, var(--primary-700) 0%, var(--primary-800) 100%);
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(90, 99, 230, 0.4);

    &::before {
      opacity: 1;
    }
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 4px 12px rgba(90, 99, 230, 0.3);
  }

  &:focus-visible {
    outline: 2px solid var(--primary-600);
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  @media (max-width: 768px) {
    padding: var(--space-3) var(--space-4);
    font-size: var(--text-sm);
  }
`;

export const SecondaryButton = styled.button`
  padding: var(--space-3) var(--space-6);
  background: var(--surface-color);
  color: var(--text-color);
  border: 1px solid var(--border-color-light);
  border-radius: var(--radius-lg);
  font-size: var(--text-base);
  font-family: var(--font-heading-regular);
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: var(--shadow-sm);
  min-height: 44px; /* Touch friendly */

  &:hover:not(:disabled) {
    background: var(--background-color);
    border-color: var(--primary-300);
    transform: translateY(-1px);
    box-shadow: var(--shadow-md);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:focus-visible {
    outline: 2px solid var(--primary-600);
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  @media (max-width: 768px) {
    padding: var(--space-3) var(--space-4);
    font-size: var(--text-sm);
  }
`;
